// Blog content for Powerz Auto Electrical
// Each post uses a `content` array of typed blocks so the renderer stays simple.
// Block types: p (paragraph), h2 (subheading), ul (bullet list), ol (numbered),
// callout (emphasis quote), cta (call-to-action paragraph at end).

export const POSTS = [
  {
    slug: "what-does-auto-electrical-diagnostic-involve",
    title: "What Does an Auto Electrical Diagnostic Actually Involve?",
    metaTitle:
      "What Does an Auto Electrical Diagnostic Involve? | Powerz Auto Electrical Sunshine Coast",
    metaDesc:
      "Wondering what happens during an auto electrical diagnostic? Powerz Auto Electrical in Kunda Park explains the step-by-step process — from fault identification to component testing.",
    keywords: [
      "auto electrical diagnostic Sunshine Coast",
      "car electrical diagnostic Kunda Park",
      "auto electrician Sunshine Coast",
    ],
    excerpt:
      "Booked a diagnostic and wondering what we actually do once we have the keys? Here's a straight-up breakdown of how we approach an auto electrical diagnostic at our Kunda Park workshop — and why getting the process right matters more than parts-swapping.",
    image: "/builds/holden-maloo-mock-up-engine-bay-ecu-harness-diagnostic-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "5 min read",
    category: "Diagnostics",
    content: [
      {
        type: "p",
        text:
          "If you've ever booked your car in for an electrical diagnostic and wondered what actually happens once you hand over the keys — you're not alone. At Powerz Auto Electrical in Kunda Park, we get this question all the time. Here's a straight-up breakdown of exactly what we do, and why it takes the time it does.",
      },
      { type: "h2", text: "Step 1: Identify What's Faulting" },
      {
        type: "p",
        text:
          "The very first thing we do is work out what's actually faulting in the vehicle. This sounds simple, but it's the most critical step — because if you don't correctly identify the fault, everything that follows is guesswork.",
      },
      {
        type: "p",
        text:
          "Once we've got a clear picture of what's playing up, we check the components directly related to that particular fault. We don't go poking around the entire vehicle randomly — we work smart.",
      },
      { type: "h2", text: "Step 2: Full Electrical Diagnostic" },
      {
        type: "p",
        text:
          "From there, we carry out a thorough diagnostic. This typically includes:",
      },
      {
        type: "ul",
        items: [
          "Checking fuses — a blown fuse is often a symptom of a deeper problem, not the cause.",
          "Reading wiring diagrams — every vehicle is different, and understanding the specific wiring layout for your make and model is essential.",
          "Scanning the vehicle — we plug in our scan tools to pull any stored fault codes.",
          "Checking individual components — testing sensors, relays, switches and modules relevant to the fault.",
        ],
      },
      { type: "h2", text: "It's a Process of Elimination" },
      {
        type: "p",
        text:
          "Here's something important to understand: auto electrical diagnosis is a process of elimination. We work through possibilities systematically, ruling things out until we land on the definitive cause.",
      },
      {
        type: "p",
        text:
          "At Powerz, we don't like to \"throw parts\" at a problem. It's tempting — and some shops do it — to just start replacing components and hope something fixes the issue. We'd rather give our customers a 100% answer. That approach might take a little longer, but it means you're not paying for parts you don't need, and the problem actually gets fixed.",
      },
      { type: "h2", text: "Why Does It Take Time?" },
      {
        type: "p",
        text:
          "Diagnosing electrical faults is a true art. It isn't as simple as black and white. It genuinely takes years of training and dedication to understand the tricks and nuances of each individual vehicle make and model. The Powerz Auto Electrical workshop brings over 10 years of hands-on auto electrical experience to every diagnostic — and we're still learning with every vehicle that rolls in.",
      },
      {
        type: "callout",
        text:
          "When you're charged for a diagnostic, we're not taking you for a ride. It genuinely does take that long, and that expertise has real value.",
      },
      { type: "h2", text: "What Happens After the Diagnostic?" },
      {
        type: "p",
        text:
          "Once we've identified the fault, we'll contact you with a clear explanation of what's wrong and what it'll take to fix it. We never spend your money without your go-ahead — because at the end of the day, it's your money, not ours.",
      },
      {
        type: "cta",
        text:
          "Think your car has an electrical fault? Book a diagnostic with Powerz Auto Electrical at our Kunda Park workshop.",
      },
    ],
  },

  {
    slug: "why-is-my-cars-electrical-system-failing",
    title: "Why Is My Car's Electrical System Failing? A Diagnostic Guide",
    metaTitle:
      "Why Is My Car's Electrical System Failing? | Powerz Auto Electrical Sunshine Coast",
    metaDesc:
      "Car electrical problems on the Sunshine Coast? Powerz Auto Electrical explains the most common causes of automotive electrical failure and what to do about them.",
    keywords: [
      "car electrical problems Sunshine Coast",
      "automotive electrical failure",
      "auto electrician Kunda Park",
    ],
    excerpt:
      "Electrical problems are frustrating — often invisible, and seemingly out of nowhere. We see them every day. Here's our guide to the most common reasons your car's electrical system might be letting you down on the Sunshine Coast.",
    image: "/builds/mercedes-amg-gt-steering-wheel-electrical-fault-diagnostic-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "6 min read",
    category: "Diagnostics",
    content: [
      {
        type: "p",
        text:
          "Electrical problems in cars can be frustrating — partly because they're often invisible, and partly because they can seem to come out of nowhere. At Powerz Auto Electrical in Kunda Park, we see electrical faults every single day. Here's our guide to the most common reasons your car's electrical system might be letting you down.",
      },
      { type: "h2", text: "The #1 Cause We're Seeing Right Now: DIY Gone Wrong" },
      {
        type: "p",
        text:
          "Right now, the most common electrical fault coming through our doors isn't a manufacturing defect or worn-out wiring — it's a DIY job that's gone sideways.",
      },
      {
        type: "p",
        text:
          "We're seeing a lot of this especially on four-wheel drives. People are giving electrical installs a crack themselves, and it's causing more problems than it should. What might have been a simple install ends up creating faults that are far more expensive to diagnose and repair.",
      },
      {
        type: "callout",
        text:
          "As we always say at Powerz: the poor man pays twice. If you don't fully understand what you're doing with automotive electrics, it's better to bring it straight to an expert and get it done right the first time.",
      },
      { type: "h2", text: "Mechanics Attempting Auto Electrical Work" },
      {
        type: "p",
        text:
          "The second big one we're seeing is mechanics having a go at auto electrical work. Auto electrics is a specialised trade — it requires specific training, specific tools and years of experience working with vehicle electrical systems. When mechanics attempt it without that background, they often don't fix the problem, and in many cases they make it significantly worse.",
      },
      {
        type: "p",
        text:
          "We've seen jobs come in where a customer paid a mechanic to fix an electrical issue, only to have it cost them two or three times as much to get it properly diagnosed and repaired by a qualified auto electrician. Don't let that be you.",
      },
      { type: "h2", text: "Other Common Causes of Electrical Failure" },
      {
        type: "p",
        text:
          "Beyond DIY and mechanic mishaps, here are the other electrical issues we diagnose regularly:",
      },
      {
        type: "ul",
        items: [
          "Loose or corroded earth connections — more common than you'd think. We once had a Commodore HSV come in with every warning light on the dash illuminated, intermittent stalling and limp home mode. The root cause? Two earth connections that hadn't been properly tightened down after an engine swap at another shop. Those loose earths had burnt part of the wiring harness and even faulted the ECU. What looked like a catastrophic problem traced back to two bolts.",
          "Blown or repeatedly blowing fuses — a fuse that keeps blowing is a sign there's a deeper fault drawing too much current. Simply replacing the fuse doesn't fix it.",
          "Faulty sensors and modules — modern vehicles rely on dozens of sensors and electronic control modules. When one fails, it can trigger a cascade of fault codes and strange behaviour.",
          "Water damage — Queensland weather is no joke. Water ingress into connectors, junction boxes or control modules can cause serious and intermittent electrical faults that are notoriously difficult to trace.",
          "Wiring damage from rodents or heat — particularly in engine bays, wiring can be damaged by heat over time or chewed through by rodents.",
        ],
      },
      { type: "h2", text: "What to Do If Your Car's Electrical System Is Playing Up" },
      {
        type: "p",
        text:
          "Bring it to a qualified auto electrician — not a mechanic, not a mate who's handy, and ideally not yourself unless you genuinely know what you're doing. The cost of a proper diagnostic is almost always less than the cost of undoing a bad repair.",
      },
      {
        type: "cta",
        text:
          "Sunshine Coast locals — bring your car to Powerz Auto Electrical in Kunda Park. We'll diagnose it properly and give you a straight answer.",
      },
    ],
  },

  {
    slug: "how-much-does-auto-electrical-diagnostic-cost",
    title: "How Much Does an Auto Electrical Diagnostic Cost?",
    metaTitle:
      "How Much Does an Auto Electrical Diagnostic Cost? | Powerz Sunshine Coast",
    metaDesc:
      "Wondering what an auto electrical diagnostic costs on the Sunshine Coast? Powerz Auto Electrical in Kunda Park breaks down pricing, time and what's included.",
    keywords: [
      "auto electrical diagnostic cost Sunshine Coast",
      "how much does a car diagnostic cost",
      "auto electrician diagnostic price Kunda Park",
    ],
    excerpt:
      "\"How much will this cost me?\" — the most common question we get. Here's an honest breakdown of what an auto electrical diagnostic costs at our Kunda Park workshop, why time is charged, and why it's almost always cheaper than the alternative.",
    image: "/gallery/sunshine-coast-auto-electrical-workshop.jpg",
    publishedAt: "2026-05-26",
    readTime: "4 min read",
    category: "Pricing",
    content: [
      {
        type: "p",
        text:
          "One of the most common questions we get at Powerz Auto Electrical is: \"How much is a diagnostic going to cost me?\" It's a fair question — and we're going to give you a straight answer.",
      },
      { type: "h2", text: "What Does a Diagnostic Cost at Powerz?" },
      {
        type: "p",
        text:
          "At Powerz Auto Electrical, we charge for one to two hours of booked labour for a standard electrical diagnostic. Our rates are based on recommended retail pricing — no hidden margins, no inflated prices.",
      },
      {
        type: "p",
        text:
          "We're transparent about this because we know a lot of people in this industry have given auto electricians a bad reputation by overcharging. That's not how we operate. We're part of the Sunshine Coast community, and we want everyone in it to be treated fairly.",
      },
      { type: "h2", text: "Why Is Diagnostic Time Charged?" },
      {
        type: "p",
        text:
          "This is where we want to be really honest with you: diagnosing automotive electrical faults is skilled, time-consuming work.",
      },
      {
        type: "p",
        text:
          "It's not a matter of plugging in a reader and getting an instant answer. A proper diagnostic involves reading wiring diagrams, testing individual components, scanning for fault codes and working through a systematic process of elimination to find the actual root cause — not just the symptom.",
      },
      {
        type: "p",
        text:
          "The Powerz Auto Electrical workshop has over 10 years of experience in auto electrics. That knowledge and expertise is what you're paying for. And that expertise is what separates a qualified auto electrician from someone who'll throw parts at your car until something works.",
      },
      { type: "h2", text: "What Happens If the Job Is Simple?" },
      {
        type: "p",
        text:
          "If we identify the fault within the diagnostic window and it's something we can fix on the spot, we'll just get it done. You won't be charged for time we didn't use.",
      },
      {
        type: "p",
        text:
          "If the fault requires parts to be ordered or further diagnostics beyond the initial booking, we'll always contact you first, explain what we've found and get your approval before doing anything further. We never spend your money without your go-ahead.",
      },
      { type: "h2", text: "Is It Worth Paying for a Diagnostic?" },
      {
        type: "p",
        text:
          "Every time. Here's why: we regularly see customers who've already spent hundreds — sometimes thousands — of dollars having parts replaced at other workshops or attempting DIY repairs, only for the problem to persist. A proper diagnostic at the start almost always costs less than the alternative.",
      },
      {
        type: "callout",
        text:
          "As we say at Powerz: the poor man pays twice. Get it diagnosed properly from day one.",
      },
      { type: "h2", text: "What About Air Conditioning Diagnostics?" },
      {
        type: "p",
        text:
          "Our A/C Service starts at $154 GST inc. — that starting price includes a nitrogen pressure test, valve reseal, high & low side pressure check and a regas, all in one visit. Any repairs are quoted first.",
      },
      {
        type: "cta",
        text:
          "Ready to get your car diagnosed properly? Book with Powerz Auto Electrical in Kunda Park.",
      },
    ],
  },

  {
    slug: "can-i-diagnose-my-own-cars-electrical-problems",
    title: "Can I Diagnose My Own Car's Electrical Problems?",
    metaTitle:
      "Can I Diagnose My Own Car's Electrical Problems? | Powerz Auto Electrical SC",
    metaDesc:
      "Thinking about diagnosing your own car's electrical problems? Powerz Auto Electrical on the Sunshine Coast explains what you can and can't safely do yourself.",
    keywords: [
      "DIY car electrical diagnosis",
      "diagnose car electrical problems yourself",
      "auto electrical Sunshine Coast",
    ],
    excerpt:
      "With YouTube tutorials and cheap OBD2 scanners everywhere, it's tempting to DIY your car's electrical diagnostics. Here's what you can safely do yourself — and where it goes wrong fast.",
    image: "/gallery/4wd-auto-electrical-fitout-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "5 min read",
    category: "DIY",
    content: [
      {
        type: "p",
        text:
          "With YouTube tutorials and cheap OBD2 scanners everywhere, it's tempting to try diagnosing your own car's electrical problems. So can you do it yourself — and should you?",
      },
      {
        type: "p",
        text:
          "Here's our honest take from 10+ years in the trade at Powerz Auto Electrical.",
      },
      { type: "h2", text: "What You Can Do Yourself" },
      {
        type: "p",
        text:
          "There are a few basic things any car owner can check before calling an auto electrician:",
      },
      {
        type: "ul",
        items: [
          "Check obvious fuses — your owner's manual will show fuse box locations. A visually blown fuse is easy to spot and safe to replace.",
          "Check battery terminals — loose or corroded terminals are a common cause of starting issues and can be cleaned and tightened at home.",
          "Use a basic OBD2 scanner — a $30–$50 scanner can pull fault codes and give you a starting point. Note: reading codes and correctly interpreting them are very different things.",
        ],
      },
      { type: "h2", text: "Where DIY Gets Dangerous (and Expensive)" },
      {
        type: "p",
        text:
          "Right now, the most common electrical fault we're seeing at Powerz is DIY jobs gone wrong — especially on four-wheel drives.",
      },
      {
        type: "p",
        text:
          "People see an install tutorial online, give it a crack, and end up creating faults that didn't exist before. Wiring up a dual battery system, adding accessories or modifying vehicle electrics without a proper understanding of the vehicle's wiring architecture can cause faults that are genuinely complex and expensive to diagnose and fix.",
      },
      {
        type: "callout",
        text:
          "The poor man pays twice. We see this constantly. Someone spends $50 in parts trying to fix something themselves, then spends $500+ having a qualified auto electrician undo the damage and fix the original problem.",
      },
      { type: "h2", text: "The Honest Truth About Electrical Diagnosis" },
      {
        type: "p",
        text:
          "Diagnosing auto electrical faults is a true art. It isn't black and white. It takes years of training and hands-on experience to understand how different vehicles behave, how their systems interact and where to look when something isn't obvious.",
      },
      {
        type: "p",
        text:
          "We had an HSV Commodore come into our Kunda Park workshop with every warning light on the dash, intermittent stalling and limp home mode. It had been to another shop for an engine conversion. To the untrained eye, it looked like a catastrophic electrical failure. We traced it back to two earth connections that hadn't been tightened properly — which had burnt part of the wiring harness and faulted the ECU. Most DIYers would never find that.",
      },
      { type: "h2", text: "When Should You Call an Auto Electrician?" },
      { type: "p", text: "Call us when:" },
      {
        type: "ul",
        items: [
          "Your check engine light is on and a code reader gives you a fault you don't understand.",
          "You have multiple warning lights on at once.",
          "Your car is behaving intermittently — starting sometimes, not others.",
          "Any electrical accessory has stopped working after a recent install or repair.",
          "You've already tried something and it hasn't worked.",
        ],
      },
      {
        type: "cta",
        text:
          "Sunshine Coast locals — if you're not 100% sure what you're doing, bring it to Powerz. We'll give you a straight answer.",
      },
    ],
  },

  {
    slug: "what-causes-most-automotive-electrical-failures",
    title: "What Causes Most Automotive Electrical Failures?",
    metaTitle:
      "What Causes Most Automotive Electrical Failures? | Powerz Auto Electrical SC",
    metaDesc:
      "From DIY wiring jobs to loose earth connections — Powerz Auto Electrical in Kunda Park reveals the most common causes of automotive electrical failure on the Sunshine Coast.",
    keywords: [
      "automotive electrical failure causes",
      "common car electrical problems",
      "auto electrician Sunshine Coast",
    ],
    excerpt:
      "After years of diagnosing automotive electrical faults at our Kunda Park workshop, patterns have emerged. Here are the most common causes of automotive electrical failure we see on the Sunshine Coast — and why each one is preventable.",
    image: "/builds/ford-fpv-falcon-engine-bay-cooling-fan-module-fault-diagnostic-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "7 min read",
    category: "Diagnostics",
    content: [
      {
        type: "p",
        text:
          "After years of diagnosing and repairing automotive electrical faults at Powerz Auto Electrical in Kunda Park, we've seen patterns emerge. Certain types of faults show up again and again. Here are the most common causes of automotive electrical failure we see on the Sunshine Coast.",
      },
      { type: "h2", text: "1. DIY Electrical Work" },
      {
        type: "p",
        text:
          "This is currently the number one cause of complex electrical faults coming through our workshop. People are attempting electrical installs themselves — particularly on four-wheel drives — and creating problems that weren't there to begin with.",
      },
      {
        type: "p",
        text:
          "It's understandable. Parts are expensive, and there's a wealth of tutorials online. But automotive electrical systems are complex, and a small mistake in wiring can create faults that take hours to diagnose. What might have cost $200 to install professionally ends up costing $600+ to diagnose and repair after a DIY attempt.",
      },
      { type: "callout", text: "The poor man pays twice — every time." },
      { type: "h2", text: "2. Mechanics Attempting Auto Electrical Repairs" },
      {
        type: "p",
        text:
          "The second most common cause we're seeing is mechanics having a go at auto electrical work that's outside their training. Auto electrics is a separate trade for a reason. Mechanics are highly skilled at what they do — but vehicle electrical systems require specific knowledge, specific tools and a very different diagnostic approach.",
      },
      {
        type: "p",
        text:
          "We've had multiple vehicles come in where a well-meaning mechanic has made a fault significantly worse by attempting an electrical repair without the right expertise. If your mechanic is telling you they can fix an electrical fault, it's worth asking whether they have a qualified auto electrician on staff.",
      },
      { type: "h2", text: "3. Loose Earth Connections" },
      {
        type: "p",
        text:
          "A real case study from our workshop: an HSV Commodore came in with every warning light on the dash, intermittent stalling and intermittent limp home mode. It had recently had an engine conversion done at another shop.",
      },
      {
        type: "p",
        text:
          "After a thorough diagnostic, we identified the problem: two earth connections that hadn't been tightened properly after the engine swap. Those loose earths had caused a partial burn in the wiring harness and had also faulted the ECU. What looked like a major electrical catastrophe traced back to two bolts not done up correctly.",
      },
      {
        type: "p",
        text:
          "Earth connections are the foundation of a vehicle's electrical system. Get them wrong, and everything suffers.",
      },
      { type: "h2", text: "4. Water Ingress" },
      {
        type: "p",
        text:
          "Queensland's climate — humidity, heavy rain and the odd flood event — creates real risk for vehicle electrical systems. Water finding its way into connectors, junction boxes or control modules can cause intermittent, hard-to-trace faults. Corrosion in connectors is a slow-burn problem that can take months to fully manifest.",
      },
      { type: "h2", text: "5. Heat Damage" },
      {
        type: "p",
        text:
          "In engine bays, wiring runs close to hot components. Over time, insulation can crack and break down, causing short circuits or open circuits that can be very difficult to locate visually.",
      },
      { type: "h2", text: "6. Repeated Fuse Failures" },
      {
        type: "p",
        text:
          "A fuse that keeps blowing is never just a fuse problem. Something in that circuit is drawing more current than it should. Replacing the fuse without finding the underlying cause is a temporary fix at best.",
      },
      { type: "h2", text: "The Takeaway" },
      {
        type: "p",
        text:
          "Most automotive electrical failures we see are either preventable or the result of repairs that weren't done properly in the first place. The solution in almost every case is the same: get a qualified auto electrician involved early, before a small problem becomes a big one.",
      },
      {
        type: "cta",
        text:
          "Powerz Auto Electrical — Kunda Park, Sunshine Coast. 95+ five-star Google reviews.",
      },
    ],
  },

  {
    slug: "how-long-does-electrical-diagnostic-take",
    title: "How Long Does an Electrical Diagnostic Take?",
    metaTitle:
      "How Long Does a Car Electrical Diagnostic Take? | Powerz Sunshine Coast",
    metaDesc:
      "Not sure how long to allow for an auto electrical diagnostic on the Sunshine Coast? Powerz Auto Electrical at Kunda Park explains typical timeframes and what affects them.",
    keywords: [
      "how long does car electrical diagnostic take",
      "auto electrical diagnostic time",
      "auto electrician Sunshine Coast",
    ],
    excerpt:
      "Need your car for work or the school run? Fair question — and we'll give you a real answer on typical diagnostic timeframes, what affects them, and what happens if it takes longer than expected.",
    image: "/builds/yaroomba-200-series-landcruiser-at-powerz-auto-electrical-workshop-kunda-park.jpg",
    publishedAt: "2026-05-26",
    readTime: "4 min read",
    category: "Diagnostics",
    content: [
      {
        type: "p",
        text:
          "One of the most practical questions we get from customers is: \"How long is this going to take?\" It's a reasonable thing to want to know — especially if you need your car for work or the school run. Here's what to expect when you bring your vehicle to Powerz Auto Electrical.",
      },
      { type: "h2", text: "Standard Diagnostic: 1–2 Hours" },
      {
        type: "p",
        text:
          "For a standard electrical diagnostic at Powerz Auto Electrical, we ask customers to allow for one to two hours of booked labour time.",
      },
      {
        type: "p",
        text:
          "This covers our initial fault identification, scanning, wiring diagram review and component testing. For the majority of electrical faults, this window is enough to reach a definitive diagnosis.",
      },
      { type: "h2", text: "What If It's a Simple Fix?" },
      {
        type: "p",
        text:
          "If we identify the fault within the diagnostic window and it's something we can resolve on the spot — whether that's a blown fuse, a faulty relay or a loose connection — we'll just get it done. You won't be charged for time we didn't use, and you'll drive away sorted.",
      },
      { type: "h2", text: "What If It Takes Longer?" },
      {
        type: "p",
        text:
          "Some faults are more complex. Intermittent electrical issues — things that only happen sometimes, or only under certain conditions — can be particularly time-consuming to diagnose. We've had vehicles where the fault took multiple sessions to fully isolate.",
      },
      {
        type: "p",
        text:
          "If a fault requires additional diagnostic time or parts need to be ordered, we'll always contact you before proceeding. We'll explain exactly what we've found, what we think needs to happen next and what it's likely to cost. Then we wait for your go-ahead.",
      },
      {
        type: "callout",
        text:
          "We never spend your money without your approval. At the end of the day, it's your money — not ours.",
      },
      { type: "h2", text: "Why Can't You Just Give Me an Exact Time?" },
      {
        type: "p",
        text:
          "Because every vehicle and every fault is different. A fault code reader might point us in a direction, but the actual diagnostic work — reading wiring diagrams, testing components, working through possibilities systematically — takes as long as it takes. We'd rather be honest about that than give you a number that turns out to be wrong.",
      },
      {
        type: "p",
        text:
          "What we can promise is that we work efficiently and we won't drag out a job unnecessarily.",
      },
      { type: "h2", text: "What Affects Diagnostic Time?" },
      {
        type: "ul",
        items: [
          "Vehicle make and model — some vehicles have more complex electrical architectures than others.",
          "Fault type — intermittent faults take longer than consistent ones.",
          "Previous repair attempts — if someone else has already been in the wiring, it adds complexity.",
          "Number of fault codes — multiple codes pointing in different directions require more systematic testing.",
        ],
      },
      { type: "h2", text: "Book With Confidence" },
      {
        type: "p",
        text:
          "When you drop your car at Powerz Auto Electrical, you'll know exactly where you stand. We'll give you a clear timeframe, keep you updated, and never do work without your approval.",
      },
      {
        type: "cta",
        text:
          "Kunda Park workshop, Sunshine Coast — book in for a proper diagnostic.",
      },
    ],
  },

  {
    slug: "what-tools-do-auto-electricians-use",
    title: "What Tools Do Auto Electricians Use for Diagnostics?",
    metaTitle:
      "What Tools Do Auto Electricians Use for Diagnostics? | Powerz Sunshine Coast",
    metaDesc:
      "Curious what tools auto electricians use to diagnose car electrical faults? Powerz Auto Electrical in Kunda Park explains — and reveals the most important tool of all.",
    keywords: [
      "auto electrical diagnostic tools",
      "what tools do auto electricians use",
      "car diagnostic equipment Sunshine Coast",
    ],
    excerpt:
      "Scan tools, multimeters, oscilloscopes, wiring diagrams — every workshop has the kit. But the most valuable diagnostic tool in our workshop isn't on the toolboard at all. Here's what actually matters when diagnosing a complex electrical fault.",
    image: "/gallery/auto-electrical-repair-diagnostics-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "5 min read",
    category: "Workshop",
    content: [
      {
        type: "p",
        text:
          "Walk into any auto electrical workshop and you'll see an array of equipment. But what does it all do, and what actually matters when diagnosing a complex electrical fault? Here's a look at what we use at Powerz Auto Electrical — and why our most valuable tool might surprise you.",
      },
      { type: "h2", text: "Scan Tools" },
      {
        type: "p",
        text:
          "Every modern workshop needs professional-grade scan tools. These go well beyond the basic OBD2 readers you can buy at an auto parts store. Our scan tools allow us to:",
      },
      {
        type: "ul",
        items: [
          "Read and clear diagnostic trouble codes (DTCs) across all vehicle systems — not just engine management.",
          "View live data streams from sensors and modules.",
          "Perform component activation tests (switching relays, testing actuators).",
          "Access manufacturer-specific data that generic readers can't see.",
        ],
      },
      {
        type: "p",
        text:
          "Having the right scan tool for the right vehicle makes a significant difference to diagnostic accuracy and speed.",
      },
      { type: "h2", text: "Multimeters" },
      {
        type: "p",
        text:
          "A quality digital multimeter is the backbone of any electrical diagnostic. We use multimeters to:",
      },
      {
        type: "ul",
        items: [
          "Test voltage at components and connections.",
          "Check resistance across circuits.",
          "Measure current draw.",
          "Test continuity in wiring circuits.",
        ],
      },
      {
        type: "p",
        text:
          "A multimeter doesn't lie — it gives you real numbers that tell you exactly what a circuit is or isn't doing.",
      },
      { type: "h2", text: "Wiring Diagrams" },
      {
        type: "p",
        text:
          "This one isn't a physical tool, but it might be the most important resource in the workshop. Every vehicle has its own unique wiring architecture, and without the correct diagram for the specific make, model and year, diagnosing electrical faults is close to impossible.",
      },
      {
        type: "p",
        text:
          "We invest in access to proper wiring diagrams because they're not optional — they're essential.",
      },
      { type: "h2", text: "Other Essential Equipment" },
      {
        type: "ul",
        items: [
          "Test lights — for quick circuit checks.",
          "Power probes — to apply power or ground directly to components for testing.",
          "Oscilloscopes — for looking at signal patterns from sensors and modules.",
          "Insulation resistance testers — for checking for wiring breakdown.",
        ],
      },
      { type: "h2", text: "Our Most Important Tool: Knowledge" },
      {
        type: "p",
        text:
          "Here's the honest truth — the biggest asset we have at Powerz Auto Electrical isn't a piece of equipment. It's knowledge.",
      },
      {
        type: "p",
        text:
          "That's what sets us apart from a lot of other workshops. And at the end of the day, that's what separates a qualified auto electrician from a mechanic. The specialised knowledge of vehicle electrical systems — built over years of hands-on experience — is what allows us to use all these tools effectively and arrive at the right answer.",
      },
      {
        type: "callout",
        text:
          "Anyone can own a scan tool. Knowing how to interpret what it's telling you — and what to do next — is a completely different skill.",
      },
      {
        type: "cta",
        text:
          "Powerz Auto Electrical — Kunda Park, Sunshine Coast. 95+ five-star Google reviews.",
      },
    ],
  },

  {
    slug: "why-wont-my-car-start-electrical-checklist",
    title: "Why Won't My Car Start? Electrical Diagnostic Checklist",
    metaTitle:
      "Why Won't My Car Start? Electrical Diagnostic Checklist | Powerz Sunshine Coast",
    metaDesc:
      "Car won't start on the Sunshine Coast? Powerz Auto Electrical in Kunda Park walks you through the most common electrical causes and what to check first.",
    keywords: [
      "car won't start electrical problem",
      "no start car diagnostic Sunshine Coast",
      "auto electrician no start vehicle Kunda Park",
    ],
    excerpt:
      "A car that won't start is one of the most stressful problems you can have — especially when you need to be somewhere. Here's the diagnostic checklist we work through at our Kunda Park workshop on every no-start vehicle.",
    image: "/builds/y62-nissan-patrol-starter-motor-replacement-mount-coolum-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "5 min read",
    category: "Diagnostics",
    content: [
      {
        type: "p",
        text:
          "A car that won't start is one of the most stressful vehicle problems you can have — especially when you're trying to get somewhere. At Powerz Auto Electrical, no-start vehicles are something we deal with regularly. Here's our diagnostic checklist to help you understand what might be going on.",
      },
      { type: "h2", text: "First: What Type of No-Start Is It?" },
      {
        type: "p",
        text:
          "Not all no-starts are the same. Before we do anything, we find out exactly what the vehicle is doing — or not doing. This tells us a lot about where to look:",
      },
      {
        type: "ul",
        items: [
          "Nothing happens when you turn the key — could be battery, starter motor, ignition switch or immobiliser.",
          "Click sound but won't crank — often a starter motor or battery issue.",
          "Cranks but won't fire — could be ignition, fuel or a sensor/module fault.",
          "Cranks and fires but immediately stalls — often immobiliser, sensor or fuelling related.",
        ],
      },
      { type: "h2", text: "The Most Common Electrical Cause: Starter Motors" },
      {
        type: "p",
        text:
          "The most common electrical fault we find on no-start vehicles is a faulty starter motor. Starter motors wear out over time, and when they go, you'll often hear a single click or nothing at all when you turn the key.",
      },
      { type: "h2", text: "The Electrical No-Start Checklist" },
      {
        type: "p",
        text: "Here are the things we work through on a no-start diagnostic:",
      },
      {
        type: "ul",
        items: [
          "Battery — is it holding charge? A battery that reads 12V sitting still can drop to nothing under load. We test under cranking conditions.",
          "Battery terminals and earth connections — loose or corroded terminals are a surprisingly common cause. A bad earth connection can prevent the starter motor from receiving enough current to operate.",
          "Starter motor — we test the starter directly to confirm it's receiving power and whether it's functioning.",
          "Immobiliser / key — a faulty key fob or immobiliser fault can prevent the vehicle from starting even when everything else is fine.",
          "Ignition switch — an electrical fault in the ignition switch can interrupt the start signal before it ever reaches the starter.",
          "Fuses and relays — we check the fuses and relays associated with the starting circuit.",
        ],
      },
      { type: "h2", text: "Do We Take On Every No-Start?" },
      {
        type: "p",
        text:
          "Honestly — not always. The first thing we do with a no-start enquiry is find out what type of vehicle it is, and then decide whether we're the best people to take it on or whether it'd be better handled by another specialist more experienced with that particular vehicle type.",
      },
      {
        type: "p",
        text:
          "What we never do is leave a customer without direction. If we can't help, we'll point you to someone who can. That's just how we operate.",
      },
      { type: "h2", text: "When to Call Powerz" },
      {
        type: "p",
        text:
          "If your car won't start and you're on the Sunshine Coast, give us a call. We'll ask you the right questions to work out what might be going on and whether we can help.",
      },
      {
        type: "cta",
        text:
          "Call Powerz Auto Electrical — workshop at Kunda Park, Sunshine Coast.",
      },
    ],
  },

  {
    slug: "how-to-know-if-you-need-ac-diagnostic",
    title: "How to Know If You Need an Automotive A/C Diagnostic",
    metaTitle:
      "How to Know If You Need a Car A/C Diagnostic | Powerz Auto Electrical SC",
    metaDesc:
      "Is your car's air conditioning not working properly? Powerz Auto Electrical in Kunda Park explains the signs that point to an electrical A/C problem — and when to book a diagnostic.",
    keywords: [
      "car AC diagnostic Sunshine Coast",
      "auto air conditioning not working",
      "car aircon electrical problem Kunda Park",
    ],
    excerpt:
      "On the Sunshine Coast, working A/C isn't a luxury — it's a necessity. Here are the four signs your car's air conditioning issue is electrical, when to book a diagnostic, and why an ARC licence matters.",
    image: "/builds/subaru-evaporator-replacement-completed-powerz-auto-electrical-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "4 min read",
    category: "Air Conditioning",
    content: [
      {
        type: "p",
        text:
          "On the Sunshine Coast, a working car air conditioner isn't a luxury — it's a necessity. But how do you know if your A/C problem is electrical, or something else? Here's how we assess it at Powerz Auto Electrical.",
      },
      { type: "h2", text: "Sign 1: Your A/C Isn't Cold At All" },
      {
        type: "p",
        text:
          "If your air conditioning is blowing warm or ambient air rather than cold air, that's the clearest sign something is wrong. It doesn't automatically mean it's an electrical fault — it could be low refrigerant, a mechanical compressor issue or an electrical problem — but it always warrants a proper diagnostic.",
      },
      { type: "h2", text: "Sign 2: You Can't Hear Your Compressor Cutting In" },
      {
        type: "p",
        text:
          "This is a big one. When you switch on your A/C, you should be able to hear (and sometimes feel) the compressor engaging — there's a subtle click and often a slight change in engine idle. If you can hear your fans running but the compressor isn't cutting in, that's typically an electrical problem. It could be the compressor clutch, a pressure switch, a wiring fault or a control module issue.",
      },
      { type: "h2", text: "Sign 3: Your Fans Aren't Working" },
      {
        type: "p",
        text:
          "If the blower fans inside the cabin aren't running at all — or only run on certain speeds — that's almost always an electrical fault. It's often a blower motor resistor, a relay or a wiring issue.",
      },
      { type: "h2", text: "Sign 4: Intermittent Cooling" },
      {
        type: "p",
        text:
          "A/C that works sometimes and not others is a classic sign of an electrical fault. Intermittent faults are often the trickiest to diagnose, but they're our specialty at Powerz.",
      },
      { type: "h2", text: "Why You Need an ARC-Licensed Auto Electrician" },
      {
        type: "p",
        text:
          "Not everyone can legally work on vehicle air conditioning systems. In Australia, you need to hold an ARC (Australian Refrigeration Council) licence to handle automotive refrigerants. At Powerz Auto Electrical, we hold the required ARC licence and have the specialised training and equipment to work on A/C systems properly and legally.",
      },
      {
        type: "callout",
        text:
          "This is why not all mechanics can (or should) be doing your A/C work.",
      },
      { type: "h2", text: "What Does an A/C Diagnostic Cost?" },
      {
        type: "p",
        text:
          "At Powerz, our A/C Service starts at $154 GST inc. and includes a nitrogen pressure test, valve reseal, high & low pressure check and a regas — no hidden extras, no surprises.",
      },
      {
        type: "cta",
        text:
          "Book your A/C diagnostic at Powerz Auto Electrical, Kunda Park.",
      },
    ],
  },

  {
    slug: "what-checked-before-ac-performance-test",
    title: "What Must Be Checked Before Testing Your Car's A/C Performance?",
    metaTitle:
      "What Must Be Checked Before Testing Car A/C Performance? | Powerz SC",
    metaDesc:
      "Before testing your car's A/C, a proper pre-check is essential. Powerz Auto Electrical in Kunda Park explains exactly what qualified technicians look at first.",
    keywords: [
      "car AC pre-test checklist",
      "auto air conditioning check Sunshine Coast",
      "AC diagnostic process Kunda Park",
    ],
    excerpt:
      "Before we ever run a full A/C performance test, there's a pre-check we always do first. Skipping it means testing a system that isn't in a state to be properly assessed. Here's exactly what we check — and why each step matters.",
    image: "/builds/toyota-landcruiser-auto-air-conditioning-regas-sunshine-coast.jpg",
    publishedAt: "2026-05-26",
    readTime: "5 min read",
    category: "Air Conditioning",
    content: [
      {
        type: "p",
        text:
          "Before we ever run a full A/C performance test at Powerz Auto Electrical, there's a pre-check process that needs to happen first. Skipping it means you could be testing a system that isn't in a state to be properly assessed. Here's exactly what we check — and why it matters.",
      },
      { type: "h2", text: "Step 1: Is the A/C Actually Cold?" },
      {
        type: "p",
        text:
          "This sounds obvious, but it's the starting point. We check whether the system is producing any cold air at all. The answer tells us a lot about where to focus our attention.",
      },
      { type: "h2", text: "Step 2: Is the Compressor Cutting In?" },
      {
        type: "p",
        text:
          "The A/C compressor is the heart of the system. Before we go any further, we confirm whether the compressor is engaging when the A/C is switched on. If it's not cutting in, that points us toward an electrical fault — whether that's a clutch issue, a pressure switch problem or a wiring fault — and we need to resolve that before any meaningful performance testing can be done.",
      },
      { type: "h2", text: "Step 3: Pressure Testing" },
      {
        type: "p",
        text:
          "This is where the technical work begins. We connect our pressure gauges to the A/C system and check both high-side and low-side pressures. This tells us:",
      },
      {
        type: "ul",
        items: [
          "Whether the system has enough refrigerant.",
          "Whether there are signs of a blockage (high pressures on one side).",
          "Whether the compressor is pumping correctly.",
          "Whether the system is pulling a vacuum (which would indicate a serious issue).",
        ],
      },
      {
        type: "p",
        text:
          "High pressures, low pressures and vacuum readings all point to different problems. Interpreting those readings correctly requires both the right equipment and the knowledge to understand what the numbers mean.",
      },
      { type: "h2", text: "Step 4: Confirming the Compressor Isn't Pulling Vacuum" },
      {
        type: "p",
        text:
          "A system pulling vacuum means refrigerant has leaked out to the point where the pressure inside is below atmospheric. Running a compressor in this state can damage it — so this is something we always check before proceeding with a regas or performance test.",
      },
      { type: "h2", text: "Why the Pre-Check Matters" },
      {
        type: "p",
        text:
          "At Powerz, we've got a pretty good success rate with A/C diagnostics — and that's because we don't skip steps. A regas on a system with an underlying fault just means the refrigerant will leak out again, and you'll be back where you started. Getting the pre-check right is what makes the diagnosis and repair stick.",
      },
      { type: "h2", text: "What's Included in Our A/C Diagnostic?" },
      {
        type: "p",
        text: "Our A/C Service (from $154 GST inc.) covers:",
      },
      {
        type: "ul",
        items: [
          "Nitrogen pressure test.",
          "Valve reseal.",
          "High & low side system pressure check.",
          "Regas (where appropriate).",
          "ARC-compliant refrigerant handling.",
        ],
      },
      {
        type: "callout",
        text: "No hidden extras. No surprises. Just a properly working A/C.",
      },
      {
        type: "cta",
        text:
          "Sunshine Coast — book your car's A/C service with Powerz Auto Electrical in Kunda Park. ARC-licensed. From $154 inc. GST.",
      },
    ],
  },

  {
    slug: "what-brake-controller-do-i-need",
    title: "What Brake Controller Do I Need for My Vehicle?",
    metaTitle:
      "What Brake Controller Do I Need? | REDARC Tow-Pro Buyer's Guide | Powerz Auto Electrical Sunshine Coast",
    metaDesc:
      "Shopping for an electric brake controller on the Sunshine Coast? Powerz Auto Electrical explains the difference between Basic, Smart and Next-Gen Tow-Pro installs, when each applies, and what a proper REDARC install actually costs.",
    keywords: [
      "electric brake controller Sunshine Coast",
      "REDARC Tow-Pro installer Sunshine Coast",
      "which brake controller do I need",
      "Tow-Pro Elite vs Liberty",
    ],
    excerpt:
      "Towing a caravan, camper trailer or boat and need an electric brake controller? Here's a plain-English guide to what a brake controller actually does, why the REDARC Tow-Pro Elite is the industry benchmark in Australia, and which install tier your vehicle falls under.",
    image: "/builds/79-series-landcruiser-redarc-towpro-install-colin-maroochydore-sunshine-coast.webp",
    publishedAt: "2026-09-08",
    readTime: "6 min read",
    category: "Towing Electrics",
    content: [
      {
        type: "p",
        text:
          "If you're towing anything with brakes — caravan, camper trailer, box trailer or boat over 750kg — you legally need an electric brake controller in the tow vehicle. Which controller you need, and what the install actually looks like on your specific vehicle, is the question we get every week at our Kunda Park workshop. Here's the straight answer.",
      },
      { type: "h2", text: "What a Brake Controller Actually Does" },
      {
        type: "p",
        text:
          "An electric brake controller sits between the vehicle's brake pedal and the trailer's electric brakes. When you press the brake pedal, the controller sends a proportional current to the trailer brakes so the trailer slows in step with the tow vehicle — no snatching, no jackknifing, no dragging the tow rig around at every set of lights.",
      },
      {
        type: "p",
        text:
          "A properly-installed controller does three things: it delivers smooth proportional braking on every pedal press, it lets you manually apply trailer-only braking with a dash switch (useful for sway control and steep descents), and it protects both the tow vehicle and trailer from overheating brakes.",
      },
      { type: "h2", text: "Why the REDARC Tow-Pro Elite Is the Australian Benchmark" },
      {
        type: "p",
        text:
          "There are cheap brake controllers on the market. There are also several premium options. In an Australian workshop context, the REDARC Tow-Pro Elite has become the industry-default choice — and for good reason.",
      },
      {
        type: "ul",
        items: [
          "Proportional braking — the controller senses actual vehicle deceleration and matches trailer brake force to it. Cheap time-based controllers just ramp up over a set delay, which is why they feel harsh.",
          "Hidden install — the Tow-Pro head unit mounts out of sight under the dash. The only visible bit is a small REDARC switch that fits into a factory blank on most late-model vehicles. Looks OEM, not aftermarket.",
          "Sealed, vibration-resistant electronics — designed for Australian conditions from the ground up. We've been installing them for years and see almost zero failures.",
          "5-year REDARC manufacturer warranty on top of our 12-month workmanship warranty on the install.",
        ],
      },
      { type: "h2", text: "Tow-Pro Elite vs Tow-Pro Liberty" },
      {
        type: "p",
        text:
          "The Tow-Pro Liberty is the budget sibling — cheaper hardware, no proportional braking, no hidden install. On paper it looks like it saves you money. In practice, most customers who fit a Liberty come back within a year wanting the Elite. If you're paying for a workshop install anyway, spending the extra $50-100 on the Elite hardware is the right call almost every time.",
      },
      { type: "h2", text: "Which Tow-Pro Install Tier Applies to Your Vehicle?" },
      {
        type: "p",
        text:
          "Powerz Auto Electrical charges three transparent install tiers depending on how much wiring work your specific vehicle needs. All prices GST included.",
      },
      {
        type: "ul",
        items: [
          "Tow-Pro Elite — Basic Install ($805). For most vehicles older than ~2020 — Hilux pre-AN130, Prado pre-J250, LandCruiser 200 Series, older Rangers, older D-MAX, older Amarok, older Patrols.",
          "Tow-Pro Elite — Smart Install ($855). For late-model CAN-bus vehicles that need a smart harness — Prado J250, LandCruiser 300, D-MAX 3rd Gen, MU-X 2nd Gen, Triton 6th Gen, Y62 Patrol, Navara D27, BT-50 3rd Gen.",
          "Tow-Pro Elite — Amarok / Next-Gen Ranger & Everest ($1,080.38). VW Amarok (both gens) and Ford Next-Gen Ranger / Everest 3rd Gen require a vehicle-specific harness and additional diagnostic time to work with the factory brake system correctly.",
        ],
      },
      {
        type: "callout",
        text:
          "Not sure which tier applies to your vehicle? Send us your rego and we'll confirm the install tier in writing before you book in. No surprises at pickup.",
      },
      { type: "h2", text: "What a Proper Tow-Pro Install Looks Like" },
      {
        type: "p",
        text:
          "Every Tow-Pro install at our Kunda Park workshop follows the same process. Vehicle in first thing in the morning, harness wired directly into the vehicle brake circuit (no tap-and-tape rubbish), Tow-Pro head unit mounted out of sight under the dash with proper anchoring, dash blank switch fitted into a factory position where available, and a calibration drive with a loaded test trailer at the end so we know it's braking correctly before you drive away.",
      },
      {
        type: "p",
        text:
          "Total workshop time — typically 3-4 hours. Same-day pickup for most Sunshine Coast customers.",
      },
      { type: "h2", text: "Common Mistakes We See From Other Installs" },
      {
        type: "ul",
        items: [
          "Head unit mounted where the driver's knees hit it — needs to be tucked up under the dash, properly anchored.",
          "Switch installed with a dash-tape blank instead of a factory-position REDARC bracket. Looks aftermarket.",
          "No calibration drive after install — controller left on factory defaults, which will never match your specific vehicle-and-trailer combo.",
          "Wired into the wrong brake signal — some cheaper installers hook into the brake light circuit instead of the actual brake pressure signal, which means the controller applies trailer brakes when you're just riding the pedal.",
        ],
      },
      { type: "h2", text: "Frequently Asked Questions" },
      {
        type: "p",
        text:
          "Do I really need proportional braking? Yes, if you're towing a caravan or heavy trailer regularly. Time-based controllers work fine for a small box trailer moved once a year — anything more serious deserves the Elite.",
      },
      {
        type: "p",
        text:
          "Can you install a Tow-Pro I've already bought? Yes — bring it in with the vehicle. We stock harnesses for most vehicles but if you've already sourced yours we're happy to fit it. Note the workmanship warranty covers our install, not the unit itself.",
      },
      {
        type: "p",
        text:
          "Is it a one-day job? Yes — book the vehicle in for a morning drop, most installs are ready for same-day pickup by mid-afternoon.",
      },
      {
        type: "cta",
        text:
          "Ready to fit a proper Tow-Pro to your tow vehicle? Book Powerz Auto Electrical at our Kunda Park workshop — REDARC Tow-Pro installers for customers from all over the Sunshine Coast.",
      },
    ],
  },

  {
    slug: "what-size-bcdc-charger-do-i-need",
    title: "What Size BCDC Charger Do I Need for My Dual Battery?",
    metaTitle:
      "What Size BCDC Charger Do I Need? | REDARC BCDC Sizing Guide | Powerz Auto Electrical Sunshine Coast",
    metaDesc:
      "Sizing a BCDC charger for a dual battery on the Sunshine Coast? Powerz Auto Electrical explains how BCDC amperage matches alternator capacity, battery size and accessory load — plus which REDARC BCDC we install on which vehicle.",
    keywords: [
      "BCDC charger size Sunshine Coast",
      "REDARC BCDC installer Sunshine Coast",
      "dual battery charger sizing",
      "BCDC1225D vs BCDC1240D",
    ],
    excerpt:
      "Fitting a dual battery to your 4x4 and wondering whether you need a 20A, 25A or 40A REDARC BCDC charger? Here's a plain-English sizing guide that covers alternator capacity, battery amp-hours, accessory load and which BCDC we install on which vehicle at our Kunda Park workshop.",
    image: "/builds/keiles-200-series-landcruiser-engine-bay-dual-battery-install-peregian-springs.jpg",
    publishedAt: "2026-09-08",
    readTime: "6 min read",
    category: "Dual Battery",
    content: [
      {
        type: "p",
        text:
          "A dual battery system is only as good as the charger driving it. Fit the wrong BCDC and you either cook the alternator, undercharge the auxiliary, or throw money at capacity you'll never use. At Powerz Auto Electrical in Kunda Park, we spec and install REDARC BCDC chargers on 4x4s every week across the Sunshine Coast — here's the sizing logic we use.",
      },
      { type: "h2", text: "What a BCDC Actually Does" },
      {
        type: "p",
        text:
          "A BCDC — Battery Charger DC-to-DC — sits between your start battery and your auxiliary battery. It takes the raw voltage coming out of your alternator (which on late-model vehicles varies wildly because of smart-charging strategies) and converts it into a stable, multi-stage charge profile the auxiliary battery actually likes.",
      },
      {
        type: "p",
        text:
          "The BCDC also protects the start battery from being flattened by auxiliary loads, protects the auxiliary from being overcharged, and — with a REDARC BCDC — includes solar input if you ever add a panel down the track. It's the brain of a properly-built dual battery system.",
      },
      { type: "h2", text: "The Sizing Rule of Thumb" },
      {
        type: "p",
        text:
          "The core sizing rule REDARC publishes and we follow in the workshop is simple:",
      },
      {
        type: "callout",
        text:
          "BCDC amperage should be roughly 20% of your auxiliary battery's amp-hour rating, capped by your alternator's spare capacity.",
      },
      {
        type: "ul",
        items: [
          "75Ah AGM auxiliary → 15-20A BCDC → BCDC1225D (25A) is the right fit.",
          "100Ah AGM auxiliary → 20-25A BCDC → BCDC1225D (25A) is our most common install.",
          "120Ah AGM Lead-Carbon or lithium 4WD-spec auxiliary → 25-40A BCDC → BCDC1240D (40A) with the extra solar input.",
          "200Ah+ auxiliary bank → dual-input Manager 30 or bigger BCDC, quoted case-by-case.",
        ],
      },
      { type: "h2", text: "Why You Can't Just Fit the Biggest BCDC" },
      {
        type: "p",
        text:
          "Bigger isn't always better. The BCDC pulls its charging current out of the vehicle's alternator, which is spinning at whatever RPM you're driving at. If you fit a 40A BCDC to a small alternator that's already running headlights, air-con, ECU, fuel pump and the factory battery, you'll load the alternator past its comfortable output and it'll cook — sometimes early, sometimes after a year of highway trips.",
      },
      {
        type: "p",
        text:
          "Every vehicle has a different spare-alternator-capacity number. Most modern 4x4s (Hilux, LandCruiser, D-MAX, Ranger) have enough headroom for a 25A BCDC without any concerns. Bigger 40A units are ideal when the vehicle has a larger alternator (V8 LandCruisers, Ford Rangers post-2018, RAM 1500s, US-spec pickups) and a bigger auxiliary bank to justify the current.",
      },
      { type: "h2", text: "Which REDARC BCDC We Install on Which Vehicle" },
      {
        type: "p",
        text:
          "Here's our default recipe for the most common under-bonnet dual battery jobs at our Kunda Park workshop. Every install is quoted in writing after we've confirmed the vehicle and accessories.",
      },
      {
        type: "ul",
        items: [
          "Hilux (all recent), Ranger PX2/PX3, D-MAX 2nd Gen, MU-X 1st Gen, Prado 150/J250 → 100Ah AGM + BCDC1225D (25A) — our most-installed combo across the Sunshine Coast.",
          "LandCruiser 200 Series V8, Y62 Patrol V8, RAM 1500, F150, Silverado → 120Ah AGM Lead-Carbon + BCDC1240D (40A) with solar input — proper V8 dual battery.",
          "LandCruiser 300 Series (smart alternator, CAN-bus profile) → 100Ah AGM + BCDC1225D wired to the ignition-live signal per REDARC's 300 Series install manual.",
          "Next-Gen Ranger / Everest / Amarok (48V mild-hybrid platforms) → vehicle-specific install using REDARC's dedicated harness; typically BCDC1225D. Book in for a written quote before purchasing hardware.",
        ],
      },
      { type: "h2", text: "Common Sizing Mistakes We See" },
      {
        type: "ul",
        items: [
          "Fitting a 40A BCDC to a small 100Ah AGM. The battery can't accept that current for long without heating up — you shorten its life for no benefit.",
          "Fitting a 12A BCDC to a 150Ah lithium bank. It'll never charge in a day's driving. Undersized.",
          "Ignoring the alternator's factory spare capacity. Bigger BCDCs work great on big alternators — on small ones they'll cook the alternator regulator eventually.",
          "Skipping the solar-input model on a customer who wants to add a panel next year. Buy the BCDC1240D once, not twice.",
        ],
      },
      { type: "h2", text: "What About Lithium?" },
      {
        type: "p",
        text:
          "For clarity — Powerz Auto Electrical specialises in under-bonnet AGM dual battery installs only. We do NOT install lithium house-banks, off-grid setups, solar systems, MPPT regulators or full 4WD touring fitouts. If lithium is what you need, a dedicated 4WD / off-grid specialist is a better fit. We're happy to point you in the right direction.",
      },
      { type: "h2", text: "Frequently Asked Questions" },
      {
        type: "p",
        text:
          "Can I fit the BCDC myself? Technically yes. Practically — it's the wrong job to learn on. Wiring a BCDC to REDARC's manufacturer spec requires proper cable sizing, ANL fusing at both ends, heat-shrunk crimp terminations, factory-look loom routing and a load-test after install. It's a workshop-day job for a specialist.",
      },
      {
        type: "p",
        text:
          "How long does an under-bonnet BCDC install take? One workshop day at our Kunda Park workshop. Vehicle in first thing, drive-away load-test at the end.",
      },
      {
        type: "p",
        text:
          "How much does it cost? Under-bonnet AGM dual battery installs are quoted in writing after we confirm your vehicle, accessories and BCDC size. Typical range for a 100Ah AGM + BCDC1225D install is around $1,200 GST inc. Every quote is written up-front — no scope creep, no surprises.",
      },
      {
        type: "cta",
        text:
          "Sunshine Coast — book Powerz Auto Electrical at our Kunda Park workshop for a proper under-bonnet dual battery install. REDARC Tow-Pro & BCDC installations, 12-month workmanship warranty.",
      },
    ],
  },

  {
    slug: "300-series-landcruiser-tow-pro-install",
    title: "300 Series LandCruiser Tow-Pro Install: What's Involved & What It Costs",
    metaTitle:
      "300 Series LandCruiser Tow-Pro Install Sunshine Coast | REDARC Tow-Pro Elite on LC300 | Powerz Auto Electrical",
    metaDesc:
      "Fitting a REDARC Tow-Pro Elite to a Toyota LandCruiser 300 Series? Powerz Auto Electrical in Kunda Park explains why the LC300 needs the Smart (CAN-bus) install, where the dash switch goes, how long it takes and the $855 all-in price.",
    keywords: [
      "300 Series LandCruiser Tow-Pro install",
      "LC300 brake controller install Sunshine Coast",
      "REDARC Tow-Pro Elite LandCruiser 300",
      "LandCruiser 300 electric brake controller",
      "Tow-Pro install Sunshine Coast",
    ],
    excerpt:
      "The LandCruiser 300 is the most common vehicle we fit a REDARC Tow-Pro Elite to at our Kunda Park workshop. Here's exactly why the LC300 needs the Smart install (not the Basic), where the switch mounts, how long it takes, what it costs and the mistakes we see from DIY and generic fitouts.",
    image: "/builds/300-series-landcruiser-dual-battery-hidden-install-sunshine-coast.jpg",
    publishedAt: "2026-06-15",
    readTime: "7 min read",
    category: "Towing Electrics",
    content: [
      {
        type: "p",
        text:
          "The Toyota LandCruiser 300 Series is built to tow — 3,500 kg braked, a 3.3L twin-turbo V6 diesel and a 10-speed auto that barely notices a 2.5-tonne van behind it. What it doesn't come with is an electric brake controller. If you're hitching up anything over 750 kg with electric brakes, Queensland law says you need one, and the REDARC Tow-Pro Elite is what we fit to LC300s every week at our Kunda Park workshop on the Sunshine Coast.",
      },
      {
        type: "p",
        text:
          "This post covers everything a 300 Series owner asks us before booking: why the LC300 needs the Smart install rather than the Basic one, where the control knob goes, what the harness looks like, how long we need the truck, what it costs, and what goes wrong when it's done on the cheap.",
      },
      { type: "h2", text: "Why the LandCruiser 300 Needs the Smart (CAN-Bus) Install" },
      {
        type: "p",
        text:
          "We price Tow-Pro Elite installs in three tiers — Basic ($805), Smart ($855) and Amarok / Next-Gen Ranger & Everest ($1,080.38). The 300 Series sits firmly in the Smart tier, and here's why.",
      },
      {
        type: "p",
        text:
          "Older vehicles give you a clean, analogue brake-light signal you can tap for the controller. The LC300 doesn't. Its brake-light circuit, stability control, trailer-sway control and lighting are all talking over CAN-bus, and the factory tow-wiring pre-provisioning is behind the rear quarter rather than at the pedal. Splice the wrong wire and you'll either trigger a dash fault, confuse the trailer-sway system, or get a brake controller that fires late — which is exactly what you don't want with a 3-tonne van pushing you downhill on the Bruce Highway.",
      },
      {
        type: "ul",
        items: [
          "Vehicle-specific REDARC harness (TPH-xxx series) — plugs into the factory trailer pre-wire, no splicing into the CAN loom.",
          "Correct ignition-switched power and ground so the unit sleeps with the vehicle and never back-feeds.",
          "Brake signal picked up from the correct point so proportional braking responds instantly, not after a lag.",
          "Dash fault-check with the scan tool before and after — the LC300 will log a code if anything is out of place.",
        ],
      },
      { type: "h2", text: "Where the Tow-Pro Control Knob Mounts on an LC300" },
      {
        type: "p",
        text:
          "The Tow-Pro Elite's selling point is that the brains sit hidden under the dash and only a small rotary knob is visible. On the 300 Series we fit a REDARC dash blank into one of the unused factory switch positions low on the driver's side — it looks like Toyota fitted it, and it's within easy reach of your right hand without taking your eyes off the road.",
      },
      {
        type: "p",
        text:
          "If your truck already has every blank filled (common on GR Sport and Sahara ZX builds with factory options), we'll talk through alternative positions on the lower dash before we drill anything. We don't put holes where you haven't agreed.",
      },
      {
        type: "callout",
        text: "On the 300 Series, a brake controller done right is invisible except for one knob. Done wrong, it's a dash fault, a flashing trailer-sway light and a van that brakes late.",
      },
      { type: "h2", text: "What's Included in the $855 Smart Install" },
      {
        type: "ul",
        items: [
          "Genuine REDARC Tow-Pro Elite EBRH-ACCV3 head unit — the ESC-compatible version your LC300 requires.",
          "Toyota-specific REDARC harness — plug-and-play into the factory tow pre-wire, no cut-and-tape.",
          "REDARC dash switch blank fitted into a factory switch position.",
          "Head unit anchored out of sight under the dash — not zip-tied to a loom.",
          "Pre- and post-install scan-tool check for stored faults.",
          "Activation and a calibration drive with a braked trailer — you don't collect the truck until it brakes properly.",
          "12-month workmanship warranty on top of REDARC's manufacturer warranty.",
        ],
      },
      {
        type: "p",
        text:
          "Price is GST included and quoted up-front. If your 300 Series is also getting a 12-pin trailer plug or an Anderson plug for a caravan's house battery, we'll bundle them into the same workshop day — see our towing electrics and Anderson plug pages for those prices.",
      },
      { type: "h2", text: "How Long Does It Take?" },
      {
        type: "p",
        text:
          "A Smart install on a 300 Series is a half-day job at our Kunda Park workshop. Drop it in first thing, and it's ready after lunch with the calibration drive done. If we're adding a 12-pin plug or Anderson plug at the same time, allow the full day.",
      },
      { type: "h2", text: "Proportional vs User-Controlled Mode on the LC300" },
      {
        type: "p",
        text:
          "The Tow-Pro Elite has two modes, and LC300 owners should use both. Proportional mode uses the unit's internal accelerometer to brake the trailer in step with the vehicle — this is what you want on the highway and for everyday towing. User-controlled mode applies a fixed braking level you set with the knob — ideal for slippery boat ramps, steep off-road descents and when you want the trailer to drag a little to keep everything straight.",
      },
      {
        type: "p",
        text:
          "We'll walk you through switching between modes and setting the gain for your specific van or trailer before you leave. A 1.8-tonne camper and a 3.4-tonne off-road van need very different settings.",
      },
      { type: "h2", text: "Mistakes We Fix on 300 Series Trucks" },
      {
        type: "ol",
        items: [
          "Generic harness or spliced wiring — causes CAN-bus faults and intermittent braking. We've pulled out installs that worked in the driveway and failed at 100 km/h.",
          "Knob mounted in the wrong spot — too high on the dash, or somewhere you have to lean to reach. It needs to be where your hand falls naturally.",
          "No calibration drive — the unit was fitted, switched on and handed back. The customer finds out it's set wrong when the van locks up at the first roundabout.",
          "Head unit loose under the dash — rattles, and eventually chafes the loom.",
          "Wrong unit — the older non-ESC Tow-Pro fitted to a vehicle that needs the ACCV3. It works until the stability control intervenes.",
        ],
      },
      { type: "h2", text: "Do I Legally Need a Brake Controller on My LandCruiser 300?" },
      {
        type: "p",
        text:
          "In Queensland, any trailer with a GTM over 750 kg must have its own brakes, and over 2,000 kg GTM it must have brakes on all wheels plus breakaway. Electric brakes need a controller in the tow vehicle. Given almost everything a 300 Series owner tows — caravans, car trailers, horse floats, large boats — falls into that bracket, the answer is yes.",
      },
      { type: "h2", text: "Book Your LC300 Tow-Pro Install on the Sunshine Coast" },
      {
        type: "p",
        text:
          "We're at 2a/3 Hitech Dr, Kunda Park — central to Maroochydore, Buderim, Mooloolaba, Caloundra and Noosa. Every 300 Series Tow-Pro install is done in-house with the Toyota-specific harness, scan-tool checked, and calibrated with a trailer before you collect it. Have a look at our 300 Series hidden dual battery + Tow-Pro build to see a finished example.",
      },
      {
        type: "cta",
        text:
          "Towing with a LandCruiser 300 on the Sunshine Coast? Call Powerz Auto Electrical on 0428 182 015 or hit Get a Quote to book your REDARC Tow-Pro Elite Smart install — $855 GST inc, done in half a day at our Kunda Park workshop.",
      },
    ],
  },
];

export const BLOG_PATH = (slug) => `/blog/${slug}`;
export const findPost = (slug) => POSTS.find((p) => p.slug === slug);
